import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  TableFooter,
  useTheme,
  CircularProgress,
  Grid,
  Button
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import axios from 'axios';
// NUEVO: importar xlsx y FileSaver
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import DownloadIcon from '@mui/icons-material/Download';

dayjs.locale('es');

const ContabilidadMensual = () => {
  const theme = useTheme();
  const [fecha, setFecha] = useState(dayjs());
  const [datos, setDatos] = useState([]);
  const [totales, setTotales] = useState({ total_pacientes: 0, total_ingresos: 0 });
  const [loading, setLoading] = useState(false);
  const API_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    const fetchData = async (mes, anio) => {
      setLoading(true);
      try {
        const res = await axios.get(`${API_URL}/api/contabilidad/mensual`, {
          params: { mes, anio },
          withCredentials: true
        });
        setDatos(res.data.dailyData);
        setTotales(res.data.totales);
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };

    const mes = fecha.month() + 1; // dayjs: enero es 0, entonces sumamos 1
    const anio = fecha.year();
    fetchData(mes, anio);
  }, [fecha, API_URL]);

  const handleFechaChange = (newFecha) => {
    setFecha(newFecha);
  };

  // Calcular totales para la tabla
  const totalEfectivo = datos.reduce((sum, dia) => sum + parseFloat(dia.efectivo), 0);
  const totalTarjeta = datos.reduce((sum, dia) => sum + parseFloat(dia.tarjeta), 0);
  const totalSinpe = datos.reduce((sum, dia) => sum + parseFloat(dia.sinpe_movil), 0);
  const totalTransferencia = datos.reduce((sum, dia) => sum + parseFloat(dia.transferencia), 0);
  const totalDiaSum = datos.reduce((sum, dia) => sum + parseFloat(dia.total_dia), 0);

  // NUEVO: Función para exportar a Excel
  const exportToExcel = () => {
    // Encabezados eliminados porque no se usan

    // Filas de datos
    const rows = datos.map(dia => ({
      fecha: dayjs(dia.fecha).format('DD/MM/YYYY'),
      num_pacientes: dia.num_pacientes,
      efectivo: parseFloat(dia.efectivo),
      tarjeta: parseFloat(dia.tarjeta),
      sinpe_movil: parseFloat(dia.sinpe_movil),
      transferencia: parseFloat(dia.transferencia),
      total_dia: parseFloat(dia.total_dia)
    }));

    // Fila de totales
    rows.push({
      fecha: 'Totales',
      num_pacientes: datos.reduce((sum, dia) => sum + parseInt(dia.num_pacientes), 0),
      efectivo: totalEfectivo,
      tarjeta: totalTarjeta,
      sinpe_movil: totalSinpe,
      transferencia: totalTransferencia,
      total_dia: totalDiaSum
    });

    // Crear hoja y libro
    const ws = XLSX.utils.json_to_sheet(rows, { header: Object.keys(rows[0]) });
    // Estilos: ancho de columnas
    ws['!cols'] = [
      { wch: 12 }, // Fecha
      { wch: 14 }, // N° Pacientes
      { wch: 16 }, // Efectivo
      { wch: 16 }, // Tarjeta
      { wch: 16 }, // Sinpe Móvil
      { wch: 16 }, // Transferencia
      { wch: 18 }, // Total Día
    ];
    // Estilos: encabezado en negrita y color
    const range = XLSX.utils.decode_range(ws['!ref']);
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const cell = ws[XLSX.utils.encode_cell({ r: 0, c: C })];
      if (cell) {
        cell.s = {
          font: { bold: true, color: { rgb: "FFFFFF" } },
          fill: { fgColor: { rgb: "1976D2" } }, // Azul primario MUI
          alignment: { horizontal: "center" }
        };
      }
    }
    // Estilos: totales en negrita
    for (let C = range.s.c; C <= range.e.c; ++C) {
      const cell = ws[XLSX.utils.encode_cell({ r: rows.length, c: C })];
      if (cell) {
        cell.s = {
          font: { bold: true },
          alignment: { horizontal: "center" }
        };
      }
    }

    // Crear libro y exportar
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Contabilidad Mensual');
    // Aplicar estilos (requiere xlsx-style o SheetJS Pro, pero el formato básico se mantiene)
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([excelBuffer], { type: 'application/octet-stream' }), `Contabilidad_Mensual_${fecha.format('MMMM_YYYY')}.xlsx`);
  };

  return (
    <Box sx={{ 
        padding: '64px 16px 16px 16px',
        minHeight: 'calc(100vh - 64px)',
        width: '100%',
        maxWidth: '100%',
        overflowX: 'hidden',
        boxSizing: 'border-box'
    }}>
      <Paper
        elevation={3}
        sx={{
            padding: { xs: 2, sm: 3 },
            width: '100%',
            overflowX: 'auto',
            borderRadius: 2,
            background: `linear-gradient(135deg, ${theme.palette.background.default} 0%,rgb(238, 245, 178) 100%)`,
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
            marginTop: '200px'
        }}
      >
        <Typography variant="h5" sx={{ 
          fontWeight: 'bold', 
          color: theme.palette.primary.main,
          mb: 2
        }}>
          Contabilidad Mensual
        </Typography>
        <Box sx={{ 
          mb: 3, 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2, 
          flexWrap: 'wrap' 
        }}>
          <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
            <DatePicker
              views={['year', 'month']}
              label="Seleccionar mes y año"
              minDate={dayjs().year(2020)}
              maxDate={dayjs()}
              value={fecha}
              onChange={handleFechaChange}
              format="MMMM YYYY"
              sx={{ 
                width: { xs: '100%', sm: 300 },
                backgroundColor: 'white',
                borderRadius: 1,
              }}
            />
          </LocalizationProvider>
          <Button
            variant="contained"
            color="success"
            startIcon={<DownloadIcon />}
            onClick={exportToExcel}
            sx={{ height: 56, fontWeight: 'bold', minWidth: 50 }}
            disabled={datos.length === 0}
          >
            Exportar a Excel
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
            <CircularProgress />
          </Box>
        ) : datos.length === 0 ? (
          <Typography variant="body1" align="center" sx={{ py: 4 }}>
            No hay datos disponibles para el mes seleccionado.
          </Typography>
        ) : (
          <>
            <TableContainer 
              component={Paper} 
                sx={{ 
                    maxHeight: '300px', // Reducir altura máxima
                    width: '100%',
                    overflowX: 'auto',
                    overflowY: 'auto',
                    boxShadow: 'none',
                    border: '1px solid rgba(0, 0, 0, 0.12)',
                    '& .MuiTable-root': {
                        minWidth: 650 // Asegura un ancho mínimo
                    }
                }}
            >
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', backgroundColor: theme.palette.primary.light, color: 'white' }}>Fecha</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: theme.palette.primary.light, color: 'white' }}>N° Pacientes</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: theme.palette.primary.light, color: 'white' }}>Efectivo</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: theme.palette.primary.light, color: 'white' }}>Tarjeta</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: theme.palette.primary.light, color: 'white' }}>Sinpe Móvil</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: theme.palette.primary.light, color: 'white' }}>Transferencia</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: theme.palette.primary.light, color: 'white', minWidth: 160 }}>Total Día</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {datos.map((dia) => (
                    <TableRow key={dia.fecha}>
                      <TableCell>{dayjs(dia.fecha).format('DD/MM/YYYY')}</TableCell>
                      <TableCell align="center">{dia.num_pacientes}</TableCell>
                      <TableCell align="center" sx={{ color: 'success.main', fontWeight: 500 }}>
                        ₡{parseFloat(dia.efectivo).toLocaleString('es-CR', { minimumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell align="center" sx={{ color: 'info.main', fontWeight: 500 }}>
                        ₡{parseFloat(dia.tarjeta).toLocaleString('es-CR', { minimumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell align="center" sx={{ color: 'secondary.main', fontWeight: 500 }}>
                        ₡{parseFloat(dia.sinpe_movil).toLocaleString('es-CR', { minimumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell align="center" sx={{ color: 'warning.main', fontWeight: 500 }}>
                        ₡{parseFloat(dia.transferencia).toLocaleString('es-CR', { minimumFractionDigits: 2 })}
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold', color: theme.palette.primary.main, minWidth: 160 }}>
                        ₡{parseFloat(dia.total_dia).toLocaleString('es-CR', { minimumFractionDigits: 2 })}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooter>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Totales</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold' }}>{datos.reduce((sum, dia) => sum + parseInt(dia.num_pacientes), 0)}</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', color: 'success.main' }}>₡{totalEfectivo.toLocaleString('es-CR', { minimumFractionDigits: 2 })}</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', color: 'info.main' }}>₡{totalTarjeta.toLocaleString('es-CR', { minimumFractionDigits: 2 })}</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', color: 'secondary.main' }}>₡{totalSinpe.toLocaleString('es-CR', { minimumFractionDigits: 2 })}</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', color: 'warning.main' }}>₡{totalTransferencia.toLocaleString('es-CR', { minimumFractionDigits: 2 })}</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 'bold', color: theme.palette.primary.main, minWidth: 160 }}>₡{totalDiaSum.toLocaleString('es-CR', { minimumFractionDigits: 2 })}</TableCell>
                  </TableRow>
                </TableFooter>
              </Table>
            </TableContainer>

            {/* Resumen mensual */}
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, backgroundColor: '#e8f5e9' }}>
                  <Typography variant="subtitle1" align="center" fontWeight="bold">
                    Total Pacientes del Mes
                  </Typography>
                  <Typography variant="h5" align="center" color="primary.main" fontWeight="bold">
                    {totales.total_pacientes}
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, backgroundColor: '#e0f7fa' }}>
                  <Typography variant="subtitle1" align="center" fontWeight="bold">
                    Total Ingresos del Mes
                  </Typography>
                  <Typography variant="h5" align="center" color="primary.main" fontWeight="bold">
                    ₡{parseFloat(totales.total_ingresos).toLocaleString('es-CR', { minimumFractionDigits: 2 })}
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default ContabilidadMensual;